/**
 * Tumbler Pin Access Evaluator
 *
 * Shared "combination lock" mechanism for ALL `gate_`-prefixed host patterns
 * (the full-page ACCESS badge reader today, future compact "safe box" widget
 * patterns later). Same 7-pin combination semantics as the tumbler pins
 * configured at the TSX host level (Component_Registry2 /
 * TUMBLER_PIN_DEFINITIONS), but evaluated here against the edge-level pins
 * carried on a single ManifestButton (Directory_Button_Assignments row) —
 * this lets one shared renderer component serve many different target
 * directories/routes, each with its own combination.
 *
 * Mechanism is intentionally decoupled from shape/input: every Gate host
 * calls evaluateTumblerAccess(button, auth) after its own badge/PIN/OTP
 * capture step succeeds, then either routes (handleManifestButtonClick) or
 * shows a denial state — the check itself never changes, only what UI
 * collected the credentials beforehand.
 *
 * REMAPPED 2026-11-04: the 11-item auth model splits into 7 frontend-
 * evaluated "blue" pins (this file) + 4 backend-only "orange" controller
 * checks (OTP-to-mount, Org Role → Department Member resolution, Org Role →
 * OrgMgtHierarchy minimum level, Manage-type exec SP success) that are
 * enforced exclusively in the stored procedure layer and have NO frontend
 * representation here. The DB column slots for pins 4-7 were ALSO renamed
 * (same day) to match their new boolean-gate meanings below — no more
 * stale dept/hierarchy/manage-auth names:
 *
 *   tumbler_pin1_route_guard:      0=open (bypass all other pins), 1=guarded
 *   tumbler_pin2_jwt_auth:         0=public, 1=JWT required
 *   tumbler_pin3_org_assoc:        0=none, 1=requires >=1 Org_PfUser association
 *   tumbler_pin4_orgrole_assigned: 0=none, 1=requires an assigned Org_Role
 *                                  ("Org Role Assigned" — department-member
 *                                  resolution is a separate orange #9 check)
 *   tumbler_pin5_isorg_admin:      0=none, 1=requires IsOrg_Admin on the
 *                                  active org ("ISOrg_Admin" — minimum
 *                                  hierarchy level is a separate orange #10 check)
 *   tumbler_pin6_isorg_superadmin: 0=none, 1=requires IsOrg_SuperAdmin on the
 *                                  active org ("ISOrg_SuperAdmin" — org-scoped,
 *                                  NOT the platform-level TG SuperAdmin)
 *   tumbler_pin7_tgsuperadmin:     0=none, 1=requires platform TG_SuperAdmin
 *                                  membership ("TG_SuperAdmin Member" — a REAL
 *                                  gate; the Manage-type exec SP check is a
 *                                  separate orange #11 check)
 *
 * A pin value of null/undefined (omitted from the manifest when NULL in the
 * DB) means "no additional check for that pin" — always treat absent the
 * same as 0/null. Pins are independent/orthogonal (no cascading "lock
 * depth" requirement) — any combination of pins 1-7 is valid.
 *
 * Created: 2026-10-29
 * Remapped: 2026-11-04
 * DB columns renamed: 2026-11-04
 */

import { useMemo } from 'react';
import type { ManifestButton } from '../TGC/ComponentTemplates/DirectoryPatterns/manifest-types';
import type { UseAuthResult } from './useAuth';

export type TumblerDenialReason =
    | 'JWT_REQUIRED'
    | 'ORG_ASSOCIATION_REQUIRED'
    | 'ORG_ROLE_REQUIRED'
    | 'ORG_ADMIN_REQUIRED'
    | 'ORG_SUPERADMIN_REQUIRED'
    | 'TG_SUPERADMIN_REQUIRED';

export interface TumblerAccessResult {
    allowed: boolean;
    /** Which pin (1-7) failed first, if denied */
    failedPin?: number;
    reason?: TumblerDenialReason;
    message?: string;
}

function getActiveRoleAssignment(auth: UseAuthResult) {
    if (!auth.activeOrganizationUUID) return null;
    return (
        auth.userRoleAssignments.find(
            (ra) => ra.organization_uuid === auth.activeOrganizationUUID,
        ) || null
    );
}

/** The Organization record matching the user's currently active organization, if any */
function getActiveOrganization(auth: UseAuthResult) {
    if (!auth.activeOrganizationUUID) return null;
    return (
        auth.userOrganizations.find(
            (org) => org.organization_uuid === auth.activeOrganizationUUID,
        ) || null
    );
}

/**
 * Evaluate a button's 7-pin tumbler combination against the current user's
 * auth context (from useAuth()). Pins are checked in order 1→7; the first
 * failing pin short-circuits the result.
 */
export function evaluateTumblerAccess(
    button: Pick<
        ManifestButton,
        | 'tumbler_pin1_route_guard'
        | 'tumbler_pin2_jwt_auth'
        | 'tumbler_pin3_org_assoc'
        | 'tumbler_pin4_orgrole_assigned'
        | 'tumbler_pin5_isorg_admin'
        | 'tumbler_pin6_isorg_superadmin'
        | 'tumbler_pin7_tgsuperadmin'
    >,
    auth: UseAuthResult,
): TumblerAccessResult {
    // Pin 1: master switch — 0/null = open, bypass every other pin
    const routeGuard = button.tumbler_pin1_route_guard ?? 0;
    if (routeGuard === 0) {
        return { allowed: true };
    }

    // Pin 2: JWT auth required
    const jwtRequired = (button.tumbler_pin2_jwt_auth ?? 0) === 1;
    if (jwtRequired && !auth.isAuthenticated) {
        return {
            allowed: false,
            failedPin: 2,
            reason: 'JWT_REQUIRED',
            message: 'Please log in to access this destination.',
        };
    }

    // Pin 3: requires at least one Org_PfUser association (simple boolean —
    // no more active/affirmed sub-levels)
    const orgAssocRequired = (button.tumbler_pin3_org_assoc ?? 0) === 1;
    if (orgAssocRequired && auth.userOrganizations.length === 0) {
        return {
            allowed: false,
            failedPin: 3,
            reason: 'ORG_ASSOCIATION_REQUIRED',
            message: 'You need to be associated with an organization to access this destination.',
        };
    }

    // Pin 4: requires an assigned Org_Role in the active organization
    // (department/deptgroup resolution itself is a separate orange item #9,
    // controller-only check)
    const orgRoleRequired = (button.tumbler_pin4_orgrole_assigned ?? 0) === 1;
    if (orgRoleRequired) {
        const activeRoleAssignment = getActiveRoleAssignment(auth);
        if (!activeRoleAssignment?.orgrole_id) {
            return {
                allowed: false,
                failedPin: 4,
                reason: 'ORG_ROLE_REQUIRED',
                message: 'You need an assigned organization role to access this destination.',
            };
        }
    }

    // Pin 5: requires IsOrg_Admin on the active organization (the old 0-6
    // hierarchy scale is a separate orange item #10, controller-only check)
    const orgAdminRequired = (button.tumbler_pin5_isorg_admin ?? 0) === 1;
    if (orgAdminRequired) {
        const activeOrg = getActiveOrganization(auth);
        if (!activeOrg?.is_organization_admin) {
            return {
                allowed: false,
                failedPin: 5,
                reason: 'ORG_ADMIN_REQUIRED',
                message: 'This destination is restricted to organization administrators.',
            };
        }
    }

    // Pin 6: requires IsOrg_SuperAdmin on the active organization (org-
    // scoped — NOT the platform-level TG SuperAdmin checked by pin 7)
    const orgSuperAdminRequired = (button.tumbler_pin6_isorg_superadmin ?? 0) === 1;
    if (orgSuperAdminRequired) {
        const activeOrg = getActiveOrganization(auth);
        if (!activeOrg?.is_organization_superadmin) {
            return {
                allowed: false,
                failedPin: 6,
                reason: 'ORG_SUPERADMIN_REQUIRED',
                message: 'This destination is restricted to organization super administrators.',
            };
        }
    }

    // Pin 7: requires platform-level TG_SuperAdmin membership (this is a
    // real gate, not advisory; the Manage-type exec SP check is a separate
    // orange item #11, controller-only check)
    const tgSuperAdminRequired = (button.tumbler_pin7_tgsuperadmin ?? 0) === 1;
    if (tgSuperAdminRequired && !auth.isSuperAdmin) {
        return {
            allowed: false,
            failedPin: 7,
            reason: 'TG_SUPERADMIN_REQUIRED',
            message: 'This destination is restricted to platform super administrators.',
        };
    }

    return { allowed: true };
}

/** Shape every Module Host V5 TAB_REGISTRY entry already carries. */
export type TumblerPinnedItem = Pick<
    ManifestButton,
    | 'tumbler_pin1_route_guard'
    | 'tumbler_pin2_jwt_auth'
    | 'tumbler_pin3_org_assoc'
    | 'tumbler_pin4_orgrole_assigned'
    | 'tumbler_pin5_isorg_admin'
    | 'tumbler_pin6_isorg_superadmin'
    | 'tumbler_pin7_tgsuperadmin'
> & { seq: number };

/**
 * Shared per-tab lock hook for Module Host V5 hosts' TAB_REGISTRY (BLOCK 5).
 * Evaluates evaluateTumblerAccess() once per tab, memoized on the registry +
 * auth, and returns a Map<seq, TumblerAccessResult> — the same shape every
 * BLOCK 5 consumes (see mod_tgc_10901_user_navigation.tsx, the reference
 * implementation). A locked tab's label still renders (with a lock icon);
 * its real component is never mounted when access.allowed === false, so no
 * data-fetch calls fire for content the user can't see.
 */
export function useTumblerTabAccess<T extends TumblerPinnedItem>(
    tabRegistry: T[],
    auth: UseAuthResult,
): Map<number, TumblerAccessResult> {
    return useMemo(
        () => new Map(tabRegistry.map((tab) => [tab.seq, evaluateTumblerAccess(tab, auth)])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tabRegistry, auth],
    );
}
